/**
 * Query keys and the cache policy behind them.
 *
 * Kept apart from the hooks so invalidation is stated once. The commonest bug
 * in a client like this is a mutation that updates the server and leaves a
 * stale day on screen — the user logs a meal, the total doesn't move, and they
 * log it again.
 */

export const keys = {
  profile: ["profile"] as const,
  day: (date: string) => ["day", date] as const,
  logs: (since?: string) => ["logs", since ?? "all"] as const,
  food: (id: string) => ["food", id] as const,
  foodSearch: (q: string) => ["food", "search", q] as const,
  /**
   * Every form of Recent, for invalidating them together.
   *
   * A prefix rather than a key: the log changes and *all* of them are stale,
   * whichever meal they were asked for and whichever shape they hold.
   */
  recent: ["food", "recent"] as const,
  /**
   * Recent, paged, per meal and per ordering.
   *
   * The meal is in the key because it is in the question — the server orders
   * the list by the seat each food takes at that meal, so lunch's answer and
   * dinner's answer are two different lists of the same foods. The sort is in
   * it for the same reason and more sharply: the server does the ordering, so
   * a cache entry shared between "by protein" and "by the clock" would hand
   * whichever asked second the other one's list and label it correctly.
   *
   * There was a second, unpaged form of this — one array of foods, for the
   * lists that showed a fixed twenty and stopped. Both of them grew a sort
   * control, and a sort over the first page ranks the wrong set of foods, so
   * they page now and the shape they shared a prefix with is gone.
   */
  recentPages: (meal: string | null, sort = "recent") =>
    ["food", "recent", "pages", meal ?? "any", sort] as const,
  /** One key per meal — the meal is the whole query, not a filter on it. */
  usuals: (meal: string) => ["food", "usuals", meal] as const,
  /**
   * The weights one person has logged one food at.
   *
   * Under `["food", …]` so a correction's blanket invalidation catches it,
   * and with its own middle segment so the queue can mark every food's
   * habits stale in one call once a write has landed — the same way it
   * treats Recent, and for the same reason: a write is what changes them.
   */
  portions: (foodId: string) => ["food", "portions", foodId] as const,
  favourites: ["food", "favourites"] as const,
  lastWeek: (daysAgo: number) => ["logs", "last-week", daysAgo] as const,
  recipes: ["recipes"] as const,
  recipe: (id: string) => ["recipe", id] as const,
  weight: (days: number) => ["weight", days] as const,
  overview: (days: number) => ["overview", days] as const,
  goal: ["goal"] as const,
  correlation: (weeks: number) => ["correlation", weeks] as const,
  /** One key per range, since the week you are looking at is the whole query. */
  plan: (from: string, to: string) => ["plan", from, to] as const,
  shopping: (from: string, to: string) => ["plan", "shopping", from, to] as const,
  /** Open batches only — a discarded or finished one is not a thing to spend. */
  batches: ["batches"] as const,
  /**
   * One pot, opened.
   *
   * Under the same prefix as the list so eating a portion can mark both stale
   * in one call — the count on the tile and the count on the detail are the
   * same number, and a screen showing "2 left" behind a sheet showing "1 left"
   * is the bug this key shape exists to make impossible.
   */
  batch: (id: string) => ["batches", id] as const,
  /**
   * A shared meal, by the token in the URL rather than by any id of ours.
   *
   * The one query in the app that runs for a visitor with no account, so it
   * must not sit under a key anything signed-in invalidates.
   */
  sharedMeal: (token: string) => ["shared", token] as const,
  corrections: (foodId: string) => ["corrections", foodId] as const,
};

/**
 * Cache lifetimes, by how volatile the data actually is.
 *
 * A day's totals change every time the user logs something, so they're
 * effectively always stale. A food item's nutrition doesn't change unless
 * somebody corrects it — refetching that on every mount is pure waste, and on
 * a phone it's the user's data plan.
 */
export const staleTime = {
  /** Recomputed on every log. Never trust a cached copy. */
  day: 0,
  /** Changes only on a correction. */
  food: 5 * 60 * 1000,
  /** Weighed once a day at most. */
  weight: 5 * 60 * 1000,
  /** Edited by the user themselves, so we know when it changes. */
  recipes: 10 * 60 * 1000,
  profile: 10 * 60 * 1000,
} as const;
